/**
 * ZM-Meldestand (backend modules/Accounting, ZM-Datenvertrag Abschnitt 3): eine Meldung je
 * Mandant, Zeitraum und Version, Zeilen je eingefrorener Erwerber-USt-IdNr. und Leistungsart.
 * Kein Datensatz, keine Übertragung.
 *
 * Resource: /api/accounting-zm-report (index, show, store = compute) plus recompute /
 * review / reopen.
 */
import type { OwnerResource } from "../../owner";
import type { VatReturnElsterOperation } from "../../accounting-vat-return";
import type { FilingPeriodEnum } from "../../accounting-owner-tax-settings";

export enum ZmReportStatusEnum {
  Draft = "draft",
  Reviewed = "reviewed",
  Submitted = "submitted",
  Rejected = "rejected",
}

export enum ZmSupplyKindEnum {
  IntraCommunitySupply = "intra_community_supply",
  OtherService = "other_service",
  TriangularTransaction = "triangular_transaction",
}

export enum ZmReviewCaseReasonEnum {
  CreditNote = "credit_note",
  BuyerVatIdMissing = "buyer_vat_id_missing",
  BuyerVatIdMalformed = "buyer_vat_id_malformed",
  CountryMismatch = "country_mismatch",
}

/** Ein Vorgang, der nicht in die Meldung eingeht, mit seinem Grund. */
export type ZmReviewCase = {
  vat_entry_id: string;
  document_id: string | null;
  reason: ZmReviewCaseReasonEnum;
  message: string;
  buyer_vat_id: string | null;
  net_amount: string;
};

export type ZmNotice = {
  code: "kz41_difference" | "reporting_party_incomplete";
  message: string;
  zm_supplies?: string;
  kz41?: string;
  /** fehlende Pflichtangaben der Unternehmerangabe */
  missing?: string[];
};

export type ZmReportEntry = {
  vat_entry_id: string;
  document_id: string | null;
  buyer_vat_id: string;
  net_amount: string;
};

export type ZmReportLine = {
  id: string;
  buyer_vat_id: string;
  country_code: string;
  supply_kind: ZmSupplyKindEnum;
  net_amount: string;
  entry_count: number;
  entries: ZmReportEntry[];
};

export interface AccountingZmReportResource {
  id: string;
  owner_id: string;
  period_year: number;
  period_month: number;
  period_type: FilingPeriodEnum;
  version: number;
  is_correction: boolean;
  status: ZmReportStatusEnum;
  source_fingerprint: string;
  review_cases: ZmReviewCase[];
  notices: ZmNotice[];
  /** ERiC-Prüfungen dieses Meldestands (ELSTER-Protokoll), neueste zuerst; nur Prüfung, nie Übermittlung */
  elster_operations?: VatReturnElsterOperation[];
  /** Unternehmerangabe zum Zeitpunkt der Berechnung, mit dem Meldestand eingefroren */
  reporting_party: {
    name: string | null;
    vat_id: string | null;
    street: string | null;
    house_number: string | null;
    postal_code: string | null;
    city: string | null;
    country_code: string | null;
  } | null;
  computed_at: string | null;
  computed_by: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
  transfer_ticket: string | null;
  lines?: ZmReportLine[];
  owner?: OwnerResource | null;
  created_at: string | null;
  updated_at: string | null;
}

export type ComputeAccountingZmReportRequestBody = {
  owner_id: string;
  period_year: number;
  period_month: number;
  /** ausdrücklich angegeben, nie aus Schwellen oder dem UStVA-Zeitraum abgeleitet */
  period_type: FilingPeriodEnum;
};
